import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, Search, X } from 'lucide-react';

import { useViewModelReady } from '../../../../hooks/useViewModel';
import { DeliveredTasksTable } from './DeliveredTasksTable';
import { parseDrillDown } from './deliveredTaskHelpers';
import { useDeliveredTasksViewModel } from './deliveredTasksViewModel';
import { DeliveryBrowser } from './DeliveryBrowser';
import { NexusErrorBanner } from './ErrorBanner';

/**
 * Per-task delivery ledger. Without a search it opens on a summary by bug that
 * drills into Nexus projects and then tasks; a search lists matching tasks.
 */
export function DeliveredTasksPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const vm = useDeliveredTasksViewModel(searchParams.get('q') ?? '');
  useViewModelReady(vm.initialize);
  // Bumped by Refresh to remount (and so reload) the current drill-down level.
  const [reloadKey, setReloadKey] = useState(0);
  const { bug, project } = parseDrillDown(searchParams);
  const searching = Boolean(vm.query.trim());

  const search = (value: string) => {
    vm.setQuery(value);
    // Keep the search in the URL so a filtered view can be shared.
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value.trim()) next.set('q', value.trim());
      else next.delete('q');
      return next;
    }, { replace: true });
  };

  const refresh = () => {
    if (searching) void vm.refresh();
    else setReloadKey((key) => key + 1);
  };

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <label className="relative block flex-1">
          <span className="sr-only">Search delivered tasks</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={vm.query}
            onChange={(event) => search(event.target.value)}
            placeholder="Task ID, source ID, bug ID, or Nexus project (P-…)"
            autoComplete="off"
            spellCheck={false}
            className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-9 font-mono text-sm text-gray-900 placeholder:font-sans placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {vm.query && (
            <button
              type="button"
              onClick={() => search('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </label>
        <button
          type="button"
          onClick={refresh}
          disabled={vm.loading}
          aria-label="Refresh"
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${vm.loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Task and source IDs match from the start; a versioned <span className="font-mono">id@version</span> finds its task.
        Digits also match a bug ID, and <span className="font-mono">P-…</span> matches every task in that Nexus project.
        Click a task ID or source location to copy it.
      </p>

      {vm.error && <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />}

      {!searching ? (
        <DeliveryBrowser key={reloadKey} bug={bug} project={project} />
      ) : vm.busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <DeliveredTasksTable vm={vm} />
      )}
    </>
  );
}
